// src/context/AuthContext.tsx
import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import toast from 'react-hot-toast';

// Configure axios
const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000/api';
axios.defaults.baseURL = API_BASE_URL;
// Note: No global timeout - chat requests can take longer, auth has specific timeouts via Promise.race

// Types
interface User {
  id: string;
  email: string;
  full_name?: string;
  organization?: string;
  created_at: string;
  last_login?: string;
  is_admin?: boolean;
  // Basic Microsoft profile fields
  department?: string;
  job_title?: string;
  office_location?: string;
  company_name?: string;
}

interface AuthTokens {
  access_token: string;
  refresh_token: string;
  token_type: string;
  expires_in?: number;
  user: User;
}

interface AuthContextType {
  user: User | null;
  isLoading: boolean;
  isAuthenticated: boolean;
  logout: () => Promise<void>;
  updateProfile: (fullName?: string, organization?: string) => Promise<void>;
  handleMicrosoftAuth: (code: string, state?: string) => Promise<void>;
}

// Create context
const AuthContext = createContext<AuthContextType | undefined>(undefined);

// Token management
const TOKEN_KEY = 'virtusense_access_token';
const REFRESH_TOKEN_KEY = 'virtusense_refresh_token';
const USER_KEY = 'virtusense_user';

const setTokens = (tokens: AuthTokens) => {
  localStorage.setItem(TOKEN_KEY, tokens.access_token);
  localStorage.setItem(REFRESH_TOKEN_KEY, tokens.refresh_token);
  localStorage.setItem(USER_KEY, JSON.stringify(tokens.user));
  axios.defaults.headers.common['Authorization'] = `Bearer ${tokens.access_token}`;
};

const clearTokens = () => {
  localStorage.removeItem(TOKEN_KEY);
  localStorage.removeItem(REFRESH_TOKEN_KEY);
  localStorage.removeItem(USER_KEY);
  delete axios.defaults.headers.common['Authorization'];
};

const getStoredTokens = () => ({
  access_token: localStorage.getItem(TOKEN_KEY),
  refresh_token: localStorage.getItem(REFRESH_TOKEN_KEY),
  user: localStorage.getItem(USER_KEY) ? JSON.parse(localStorage.getItem(USER_KEY)!) : null
});

// Auth Provider
export const AuthProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const navigate = useNavigate();

  // Initialize auth on mount
  useEffect(() => {
    const initAuth = async () => {
      const { access_token, user: storedUser } = getStoredTokens();

      if (access_token && storedUser) {
        axios.defaults.headers.common['Authorization'] = `Bearer ${access_token}`;

        // Verify token is still valid with timeout
        try {
          const response: any = await Promise.race([
            axios.get('/auth/me'),
            new Promise((_, reject) =>
              setTimeout(() => reject(new Error('Token validation timeout')), 5000)
            )
          ]);
          setUser(response.data);
          localStorage.setItem(USER_KEY, JSON.stringify(response.data));
        } catch (error: any) {
          console.error('Token validation failed:', error);

          // If server is unreachable or token invalid, try refresh
          const refresh_token = localStorage.getItem(REFRESH_TOKEN_KEY);
          if (refresh_token) {
            try {
              const refreshResponse: any = await Promise.race([
                axios.post('/auth/refresh', { refresh_token }),
                new Promise((_, reject) =>
                  setTimeout(() => reject(new Error('Token refresh timeout')), 5000)
                )
              ]);
              setTokens(refreshResponse.data);
              setUser(refreshResponse.data.user);
            } catch (refreshError: any) {
              console.error('Token refresh failed:', refreshError);
              // Clear everything on refresh failure
              clearTokens();
              setUser(null);
              // Don't redirect on initial load, just clear auth state
            }
          } else {
            // No refresh token, clear everything
            clearTokens();
            setUser(null);
          }
        }
      }

      setIsLoading(false);
    };

    initAuth();
  }, []);

  // Set up axios interceptor for token refresh
  useEffect(() => {
    const interceptor = axios.interceptors.response.use(
      (response) => response,
      async (error) => {
        const originalRequest = error.config;

        if (error.response?.status === 401 && !originalRequest._retry) {
          originalRequest._retry = true;

          const refresh_token = localStorage.getItem(REFRESH_TOKEN_KEY);
          if (refresh_token) {
            try {
              const response = await axios.post('/auth/refresh', { refresh_token });
              setTokens(response.data);
              setUser(response.data.user);

              // Retry original request with new token
              originalRequest.headers['Authorization'] = `Bearer ${response.data.access_token}`;
              return axios(originalRequest);
            } catch (refreshError) {
              // Refresh failed - tokens are invalid, clear and redirect
              console.error('Token refresh failed in interceptor:', refreshError);
              clearTokens();
              setUser(null);
              toast.error('Session expired. Please login again.');
              navigate('/login');
              return Promise.reject(refreshError);
            }
          } else {
            // No refresh token available
            clearTokens();
            setUser(null);
            toast.error('Authentication required. Please login.');
            navigate('/login');
            return Promise.reject(error);
          }
        }

        return Promise.reject(error);
      }
    );

    return () => {
      axios.interceptors.response.eject(interceptor);
    };
  }, [navigate]);

  // Password-based authentication methods removed
  // Only Microsoft SSO authentication is supported

  const logout = async () => {
    try {
      const refresh_token = localStorage.getItem(REFRESH_TOKEN_KEY);
      if (refresh_token) {
        await axios.post('/auth/logout', { refresh_token });
      }
    } catch (error) {
      console.error('Logout API error:', error);
      // Continue with logout even if API call fails
    } finally {
      clearTokens();
      setUser(null);
      toast.success('Logged out successfully');
      navigate('/login');
    }
  };

  const updateProfile = async (fullName?: string, organization?: string) => {
    try {
      const response = await axios.put('/auth/me', { 
        full_name: fullName,  // Send as snake_case
        organization 
      });
      
      setUser(response.data);
      localStorage.setItem(USER_KEY, JSON.stringify(response.data));
      
      toast.success('Profile updated successfully');
    } catch (error: any) {
      const message = error.response?.data?.detail || 'Update failed';
      toast.error(message);
      throw new Error(message);
    }
  };

  // Password change functionality removed - not applicable with Microsoft SSO

  const handleMicrosoftAuth = async (code: string, state?: string) => {
    try {
      const response = await axios.post('/auth/microsoft/callback', { 
        code,
        state 
      });
      
      const tokens = response.data as AuthTokens;
      setTokens(tokens);
      setUser(tokens.user);
      
      toast.success('Welcome! Successfully signed in with Microsoft.');
      navigate('/chat');
    } catch (error: any) {
      const message = error.response?.data?.detail || 'Microsoft authentication failed';
      toast.error(message);
      throw new Error(message);
    }
  };

  const value = {
    user,
    isLoading,
    isAuthenticated: !!user,
    logout,
    updateProfile,
    handleMicrosoftAuth
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

// Hook to use auth context
export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};